import { useState } from "react";

import { ChevronDown, ChevronRight, Search } from "lucide-react";
import classnames from "classnames";
import { ComponentSpinner } from "@components/spinner";
import PaginationBar from "@components/datatable/pagination-bar";
import { formatDateTime } from "@helpers/date";
import useAuditLog from "./use-audit-log";

const SCOPE_TABS = [
    { id: "all", label: "All" },
    { id: "ops", label: "Ops" },
    { id: "brand", label: "Brand" },
    { id: "publisher", label: "Publisher" }
];

const ROLE_TONE = {
    ops      : "bg-info/10 text-info",
    brand    : "bg-success/10 text-success",
    publisher: "bg-warning/15 text-warning-foreground"
};

const METHOD_TONE = {
    GET   : "bg-info/10 text-info",
    POST  : "bg-success/10 text-success",
    PATCH : "bg-warning/15 text-warning-foreground",
    PUT   : "bg-warning/15 text-warning-foreground",
    DELETE: "bg-danger/10 text-danger"
};

const ROWS_PER_PAGE_OPTIONS = [20, 50, 100];

const AuditLog = () => {
    const { entries, counts, pagination, loading, scope, setScope, search, setSearch, handlePageChange, handlePerRowsChange } = useAuditLog();
    const [expandedId, setExpandedId] = useState(null);

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-base font-semibold text-gray-900">Audit log</h2>
            <p className="mt-1 text-sm text-muted">Every action across Ops, Brand and Publisher portals, timestamped.</p>

            <div className="mt-4 inline-flex rounded-lg bg-light p-1">
                {SCOPE_TABS.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => setScope(tab.id)}
                        className={classnames(
                            "rounded-md px-4 py-2 text-sm font-medium transition-colors",
                            scope === tab.id ? "bg-white text-gray-900 shadow-sm" : "text-muted hover:text-gray-700"
                        )}>
                        {tab.label} <span className="text-muted">{counts[tab.id] ?? 0}</span>
                    </button>
                ))}
            </div>

            <div className="mt-4 flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2.5">
                <Search className="h-4 w-4 text-muted" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by user, entity or action…"
                    className="w-full border-none text-sm text-gray-700 placeholder:text-muted focus:outline-none" />
            </div>

            {loading && (
                <div className="flex justify-center py-16">
                    <ComponentSpinner />
                </div>
            )}

            {!loading && (
                <div className="mt-2 divide-y divide-gray-100">
                    {entries.map((entry) => {
                        const actor = entry.ops || entry[entry.actor_type] || {};
                        const isExpanded = expandedId === entry._id;
                        const hasDetails = Boolean(entry.method || entry.path || entry.params || entry.body);
                        return (
                            <div key={entry._id} className="py-4">
                                <div className="flex items-start gap-3">
                                    <span className={classnames("mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full", entry.success === false ? "bg-danger" : "bg-accent")} />
                                    <div className="flex-1">
                                        <div className="flex flex-wrap items-center gap-2 text-sm">
                                            <span className={classnames("rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide", ROLE_TONE[entry.actor_type] || "bg-light text-muted")}>
                                                {entry.actor_type}
                                            </span>
                                            <span className="font-medium text-gray-900">{actor.name || "—"}</span>
                                            <span className="text-muted">→</span>
                                            <span className="text-gray-700">{entry.entity_name || "—"}</span>
                                            {entry.success === false && (
                                                <span className="rounded-full bg-danger/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-danger">
                                                    Failed{entry.status_code ? ` · ${entry.status_code}` : ""}
                                                </span>
                                            )}
                                        </div>
                                        <p className="mt-1 text-sm text-muted">{entry.message}</p>
                                        <p className="mt-1 font-mono text-[11px] text-muted/80">{entry.action}</p>
                                    </div>
                                    <span className="shrink-0 whitespace-nowrap text-xs text-muted">{formatDateTime(entry.createdAt)}</span>
                                    {hasDetails && (
                                        <button
                                            type="button"
                                            onClick={() => setExpandedId(isExpanded ? null : entry._id)}
                                            className="shrink-0 rounded-md p-1 text-muted hover:bg-light hover:text-gray-700"
                                            aria-label={isExpanded ? "Hide details" : "Show details"}>
                                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                        </button>
                                    )}
                                </div>

                                {isExpanded && hasDetails && (
                                    <div className="ml-4 mt-3 space-y-2 rounded-lg border border-gray-100 bg-light p-3 text-xs">
                                        <div className="flex flex-wrap items-center gap-2">
                                            {entry.method && (
                                                <span className={classnames("rounded px-1.5 py-0.5 font-mono text-[11px] font-semibold", METHOD_TONE[entry.method] || "bg-gray-200 text-gray-700")}>
                                                    {entry.method}
                                                </span>
                                            )}
                                            {entry.path && <span className="break-all font-mono text-muted">{entry.path}</span>}
                                            {entry.status_code && (
                                                <span className={classnames("ml-auto rounded px-1.5 py-0.5 font-mono text-[11px] font-semibold", entry.success === false ? "bg-danger/10 text-danger" : "bg-success/10 text-success")}>
                                                    {entry.status_code}
                                                </span>
                                            )}
                                        </div>
                                        {entry.params && Object.keys(entry.params).length > 0 && (
                                            <div>
                                                <p className="font-semibold uppercase tracking-wide text-muted">Params</p>
                                                <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all font-mono text-[11px] text-gray-700">{JSON.stringify(entry.params, null, 2)}</pre>
                                            </div>
                                        )}
                                        {entry.body && Object.keys(entry.body).length > 0 && (
                                            <div>
                                                <p className="font-semibold uppercase tracking-wide text-muted">Body</p>
                                                <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all font-mono text-[11px] text-gray-700">{JSON.stringify(entry.body, null, 2)}</pre>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {entries.length === 0 && (
                        <p className="py-8 text-center text-sm text-muted">No matching activity yet.</p>
                    )}
                </div>
            )}

            {pagination.total > pagination.limit && (
                <div className="mt-2">
                    <PaginationBar
                        pageIndex={pagination.page - 1}
                        pageSize={pagination.limit}
                        total={pagination.total}
                        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                        onPageChange={(pageIndex) => handlePageChange(pagination.limit, pageIndex + 1)}
                        onRowsPerPageChange={handlePerRowsChange} />
                </div>
            )}
        </div>
    );
};

export default AuditLog;
